import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { DocArticle } from "@itaas/pep-contracts";
import { getDocs } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";

const CATEGORY_LABELS: Record<string, string> = {
  overview: "Vue d'ensemble",
  user: "Utilisateur",
  admin: "Administrateur",
  developer: "Développeur",
  api: "API",
  integration: "Intégration",
};

export function DocsPage() {
  const [articles, setArticles] = useState<Omit<DocArticle, "content">[]>([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    getDocs().then(setArticles).catch(console.error);
  }, []);

  const categories = ["all", ...new Set(articles.map((a) => a.category))];
  const filtered =
    filter === "all" ? articles : articles.filter((a) => a.category === filter);

  const byProduct = filtered.reduce<Record<string, typeof articles>>((acc, a) => {
    (acc[a.productSlug] ??= []).push(a);
    return acc;
  }, {});

  return (
    <SiteLayout>
      <SeoHead
        title="Documentation ITaaS"
        description="Documentation centralisée — guides utilisateur, architecture, API et intégrations de la suite ITaaS."
        keywords={["documentation ITaaS", "guide ERP RDC", "API ITaaS"]}
        canonicalPath="/docs"
      />
      <div className="pep-page-hero py-5 mb-0">
        <div className="container">
          <h1 className="display-5 fw-bold mb-2">Documentation</h1>
          <p className="lead mb-0 opacity-90">
            {articles.length} articles agrégés depuis la suite — mis à jour automatiquement.
          </p>
        </div>
      </div>
      <div className="container py-5">
        <div className="d-flex flex-wrap gap-2 mb-4">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`btn btn-sm ${filter === cat ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setFilter(cat)}
            >
              {cat === "all" ? "Tout" : (CATEGORY_LABELS[cat] ?? cat)}
            </button>
          ))}
        </div>

        {Object.entries(byProduct).map(([slug, items]) => (
          <section className="mb-5" key={slug}>
            <h2 className="h5 fw-bold mb-3">
              <Link to={`/products/${slug}`}>{items[0]?.productName ?? slug}</Link>
            </h2>
            <div className="list-group">
              {items.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/docs/${doc.id}`}
                  className="list-group-item list-group-item-action"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold">{doc.title}</div>
                      <div className="small text-secondary">{doc.excerpt}</div>
                    </div>
                    <span className="badge text-bg-light border ms-2">
                      {CATEGORY_LABELS[doc.category] ?? doc.category}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {articles.length === 0 && (
          <div className="alert alert-warning">
            Aucun article — exécutez <code>pnpm docs:scan</code> puis rafraîchissez l&apos;API.
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
